import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface FAQItem { question: string; question_bn?: string; answer: string; answer_bn?: string; }
interface FAQCategory { category: string; category_bn?: string; items: FAQItem[]; }

interface FAQData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  faq_items: FAQCategory[];
  cta_title: string; cta_title_bn?: string;
  cta_subtitle: string; cta_subtitle_bn?: string;
}

const defaultFaqData: FAQCategory[] = [
  { category: 'Orders & Payments', items: [
    { question: 'How do I place an order?', answer: 'Browse our products, add your desired items to the cart, and proceed to checkout.' },
    { question: 'What payment methods do you accept?', answer: 'We accept Cash on Delivery (COD), bKash, Nagad, and major debit/credit cards.' },
  ]},
  { category: 'Shipping & Delivery', items: [
    { question: 'How long does delivery take?', answer: 'Inside Dhaka: 1\u20132 business days. Outside Dhaka: 3\u20135 business days.' },
    { question: 'How can I track my order?', answer: 'Visit our Track Order page and enter your order number to see real-time delivery status.' },
  ]},
  { category: 'Returns & Refunds', items: [
    { question: 'What is your return policy?', answer: 'We accept returns within 7 days of delivery for items that are defective, damaged, or not as described.' },
    { question: 'When will I receive my refund?', answer: 'Refunds are processed within 5\u20137 business days after the returned item is received.' },
  ]},
];

const FAQ: React.FC = () => {
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [cms, setCms] = useState<FAQData | null>(null);

  useEffect(() => {
    fetch(`${API}/pages/faq/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCms(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const hero_title = t(cms?.hero_title, cms?.hero_title_bn, 'Frequently Asked Questions');
  const hero_subtitle = t(cms?.hero_subtitle, cms?.hero_subtitle_bn, 'Find quick answers to common questions about orders, shipping, returns, and more.');
  const faqItems = cms?.faq_items || defaultFaqData;
  const cta_title = t(cms?.cta_title, cms?.cta_title_bn, 'Still have questions?');
  const cta_subtitle = t(cms?.cta_subtitle, cms?.cta_subtitle_bn, 'Our support team is happy to help you.');

  const toggle = (key: string) => setOpenIndex(prev => (prev === key ? null : key));

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        {faqItems.map((group, gi) => (
          <div className="static-section" key={gi}>
            <span className="section-tag">{isBn ? (group.category_bn || group.category) : group.category}</span>
            <div className="faq-list">
              {group.items.map((item, qi) => {
                const key = `${gi}-${qi}`;
                const isOpen = openIndex === key;
                return (
                  <div className="faq-item" key={key}>
                    <button className={`faq-question ${isOpen ? 'open' : ''}`} onClick={() => toggle(key)} aria-expanded={isOpen}>
                      <span>{isBn ? (item.question_bn || item.question) : item.question}</span>
                      <span className="faq-chevron">{'\u25BE'}</span>
                    </button>
                    {isOpen && <div className="faq-answer">{isBn ? (item.answer_bn || item.answer) : item.answer}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <hr className="static-divider" />

        <div className="static-section" style={{ textAlign: 'center' }}>
          <h2>{cta_title}</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>{cta_subtitle}</p>
          <Link to="/contact" style={{ display: 'inline-block', padding: '12px 30px', background: 'var(--primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, textDecoration: 'none', transition: 'background var(--transition)' }}>
            {isBn ? '\u09B8\u09BE\u09AA\u09CB\u09B0\u09CD\u099F\u09C7 \u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u0995\u09B0\u09C1\u09A8' : 'Contact Support'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
